import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { Overline } from "@/components/ui-bits";
import { Loader2, Radar, Gavel, Trophy, Rocket, DollarSign, TrendingUp } from "lucide-react";

const ICONS = { leads: Radar, bids: Gavel, awards: Trophy, shipped: Rocket, revenue: DollarSign };
const COLORS = { leads: "#38BDF8", bids: "#7C9CFF", awards: "#FBBF24", shipped: "#00E5A0", revenue: "#34D399" };

export default function Autonomy() {
  const [data, setData] = useState(null);
  const load = () => api.get("/autonomy/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useRealtime(() => load());

  if (!data) return <div className="hud-card rounded-lg p-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;

  const max = Math.max(...data.funnel.filter((f) => !f.money).map((f) => f.value), 1);
  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="autonomy-page">
      <div>
        <Overline>The Money Loop</Overline>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Autonomy Dashboard</h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">The full agentic loop at a glance — leads scouted, bids placed, awards won, sites shipped, revenue earned.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={DollarSign} label="Revenue" value={money(k.revenue)} accent="#34D399" testid="kpi-revenue" />
        <Kpi icon={TrendingUp} label="Ad spend" value={money(k.ad_spend)} accent="#FBBF24" testid="kpi-adspend" />
        <Kpi icon={Rocket} label="Active ad campaigns" value={k.ad_active} accent="#7C9CFF" testid="kpi-adactive" />
        <Kpi icon={Gavel} label="Deliveries" value={k.deliveries} accent="#38BDF8" testid="kpi-deliveries" />
      </div>

      <div className="hud-card rounded-lg p-6" data-testid="autonomy-funnel">
        <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-4">Pipeline funnel</div>
        <div className="space-y-3">
          {data.funnel.map((f) => {
            const Icon = ICONS[f.key] || Radar;
            const c = COLORS[f.key] || "#38BDF8";
            const pct = f.money ? 100 : Math.round((f.value / max) * 100);
            return (
              <div key={f.key} className="flex items-center gap-3" data-testid={`funnel-${f.key}`}>
                <div className="w-32 shrink-0 flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: c }} />
                  <span className="text-sm text-slate-300">{f.label}</span>
                </div>
                <div className="flex-1 h-8 rounded-md bg-slate-900/60 overflow-hidden relative">
                  <div className="h-full rounded-md transition-all duration-700 flex items-center px-3"
                    style={{ width: `${Math.max(pct, 8)}%`, background: `${c}22`, borderRight: `2px solid ${c}` }}>
                    <span className="font-mono text-sm font-bold tabular-nums" style={{ color: c }}>
                      {f.money ? money(f.value) : f.value}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="hud-card rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4" style={{ color: accent }} /><span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</span></div>
      <div className="font-display text-2xl font-extrabold" style={{ color: accent }}>{value}</div>
    </div>
  );
}
